import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { AlertController, IonicModule, ModalController } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import { DataService } from '../../data.service';

/**
 *  DÍSZPÉLDÁNY KIÁLLÍTÁSA egy digitális bizonyítványról (backend/diszpeldany.js).
 *
 *  Nkt. 57. § (6b): csak kérelemre (rendszerben beadott díszpéldány-kérvény vagy papíron beadott
 *  kérelem); a díszpéldányon egyedi azonosító és „hiteles másolat” jelölés; a kiállító és az
 *  igazgató eredeti aláírása, körbélyegző. Az első példány ingyenes, a második térítéses.
 */
@Component({
  selector: 'app-diszpeldany-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule],
  template: `
<ion-header>
  <ion-toolbar>
    <ion-title>Díszpéldány – {{ adat?.bizonyitvany?.tanulo_nev || '' }}</ion-title>
    <ion-buttons slot="end"><ion-button (click)="bezar()">Bezárás</ion-button></ion-buttons>
  </ion-toolbar>
</ion-header>
<ion-content class="ion-padding">
  <div *ngIf="betolt" class="ion-text-center"><ion-spinner name="crescent"></ion-spinner></div>
  <ng-container *ngIf="adat && !betolt">
    <p class="halvany">
      {{ adat.bizonyitvany.nev }} · {{ adat.bizonyitvany.oktazon }}.
      A díszpéldány a digitális bizonyítvány hiteles papír alapú másolata: egyedi azonosítóval és QR-kóddal
      ellenőrizhető. Nyomtatás után a kiállító és az igazgató aláírja, és rákerül a körbélyegző.
    </p>

    <h3>Kiállított díszpéldányok ({{ adat.bizonyitvany.tanev }})</h3>
    <p class="halvany" *ngIf="!adat.diszpeldanyok.length">Ebből a bizonyítványból még nem állítottak ki díszpéldányt.</p>
    <div class="dp" *ngFor="let d of adat.diszpeldanyok" [class.ervenytelen]="+d.ervenytelen">
      <div>
        <b>{{ d.azonosito }}</b> · {{ d.sorszam }}. példány{{ +d.dijkoteles ? ' (térítéses)' : ' (ingyenes)' }}
        · {{ d.kiallitva | date:'yyyy.MM.dd. HH:mm' }}{{ d.kiallito ? ', ' + d.kiallito : '' }}
        <div class="halvany">Kérelem: {{ d.kerelem_mod === 'rendszer' ? 'a rendszerben beadott kérvény' : 'papíron' }}{{ d.kerelem_datum ? ', ' + (d.kerelem_datum | date:'yyyy.MM.dd.') : '' }} · {{ d.formatum }}</div>
        <div class="halvany" *ngIf="d.hatarozat_id">Határozat: van{{ d.hatarozat_iktatoszam ? ' (' + d.hatarozat_iktatoszam + ')' : '' }}{{ d.hatarozat_datum ? ', ' + (d.hatarozat_datum | date:'yyyy.MM.dd.') : '' }}</div>
        <div class="halvany" *ngIf="!d.hatarozat_id && d.kerelem_mod === 'rendszer'">Határozat: nincs a kiállításkor</div>
        <div class="piros" *ngIf="+d.ervenytelen">Érvénytelenítve {{ d.ervenytelenitve | date:'yyyy.MM.dd.' }} – {{ d.ervenytelenites_oka }}</div>
        <div *ngIf="d.dij && d.dij.allapot !== 'ingyenes'" [class]="'dij ' + d.dij.allapot">
          Díj: {{ d.dij.osszeg != null ? (d.dij.osszeg | number:'1.0-0') + ' Ft' : '–' }} · {{ dijAllapotNev(d.dij) }}{{ d.dij.megjegyzes ? ' – ' + d.dij.megjegyzes : '' }}
        </div>
      </div>
      <div class="gombok" *ngIf="!+d.ervenytelen">
        <ion-button size="small" fill="outline" (click)="pdf(d)"><ion-icon slot="start" name="print-outline"></ion-icon>Nyomtatás</ion-button>
        <ion-button size="small" fill="clear" color="danger" (click)="ervenytelenit(d)">Érvénytelenítés</ion-button>
      </div>
    </div>

    <h3>Új díszpéldány</h3>
    <p class="figyel" *ngIf="adat.kovetkezo_dijkoteles">
      Ebből a bizonyítványból már van érvényes díszpéldány: a következő példány <b>térítéses</b>
      ({{ adat.kovetkezo_dij?.osszeg | number:'1.0-0' }} Ft – {{ adat.kovetkezo_dij?.jogalap }}).
    </p>
    <ion-list lines="none" *ngIf="adat.kovetkezo_dijkoteles && adat.kovetkezo_dij?.osszeg > 0">
      <ion-radio-group [(ngModel)]="dijMod">
        <ion-item><ion-radio value="egyenleg" labelPlacement="end">A díj a család egyenlegébe kerül (fizetendő tétel)</ion-radio></ion-item>
        <ion-item><ion-radio value="befizetve" labelPlacement="end">Helyben befizették (kiírás és befizetés egyszerre)</ion-radio></ion-item>
        <ion-item><ion-radio value="nincs" labelPlacement="end">Nem írom ki (más úton rendezik)</ion-radio></ion-item>
      </ion-radio-group>
      <ion-item *ngIf="dijMod === 'nincs'">
        <ion-input label="Megjegyzés (kötelező: hol, hogyan rendezték)" label-placement="stacked" [(ngModel)]="dijMegjegyzes"></ion-input>
      </ion-item>
    </ion-list>
    <ion-list lines="none">
      <ion-radio-group [(ngModel)]="kerelemMod">
        <ion-item>
          <ion-radio value="rendszer" [disabled]="!adat.kervenyek.length" labelPlacement="end">
            A rendszerben beadott díszpéldány-kérvény
            <span class="halvany" *ngIf="!adat.kervenyek.length"> – a tanulónak és gondviselőinek nincs ilyen kérvénye</span>
          </ion-radio>
        </ion-item>
        <ion-item *ngIf="kerelemMod === 'rendszer' && adat.kervenyek.length">
          <ion-select label="Kérvény" label-placement="stacked" interface="popover" [(ngModel)]="kervenyId">
            <ion-select-option *ngFor="let k of adat.kervenyek" [value]="k.id">{{ k.datum }} · {{ k.user }} · {{ k.sablon }} · {{ k.hatarozat_id ? 'határozat: van' : 'határozat: nincs' }}</ion-select-option>
          </ion-select>
        </ion-item>
        <ion-item *ngIf="kerelemMod === 'rendszer' && valasztottKerveny" lines="none">
          <ion-note [color]="valasztottKerveny.hatarozat_id ? 'success' : 'warning'">
            <ng-container *ngIf="valasztottKerveny.hatarozat_id; else nincsHatarozat">
              A kérvényre határozat született{{ valasztottKerveny.hatarozat_iktatoszam ? ' (' + valasztottKerveny.hatarozat_iktatoszam + ')' : '' }}{{ valasztottKerveny.hatarozat_datum ? ', ' + (valasztottKerveny.hatarozat_datum | date:'yyyy.MM.dd.') : '' }} – a díszpéldány nyilvántartása hivatkozik rá.
            </ng-container>
            <ng-template #nincsHatarozat>A kérvényre még nincs határozat (HATÁROZAT-DISZPELDANY) – a díszpéldány kiállítható, de a nyilvántartásban nem lesz határozat-hivatkozás.</ng-template>
          </ion-note>
        </ion-item>
        <ion-item><ion-radio value="papir" labelPlacement="end">Papíron (vagy más úton) beadott kérelem</ion-radio></ion-item>
        <ion-item *ngIf="kerelemMod === 'papir'">
          <ion-input type="date" label="A kérelem dátuma" label-placement="stacked" [(ngModel)]="kerelemDatum"></ion-input>
        </ion-item>
      </ion-radio-group>
      <ion-item>
        <ion-select label="Lapméret" label-placement="stacked" interface="popover" [(ngModel)]="formatum">
          <ion-select-option value="A4">A4</ion-select-option>
          <ion-select-option value="A5">A5 (díszpéldány-nyomtatvány)</ion-select-option>
        </ion-select>
      </ion-item>
    </ion-list>
    <div class="gombok">
      <ion-button (click)="kiallit()" [disabled]="fut || adat.bizonyitvany.ervenytelen || !kerelemMod || (kerelemMod === 'rendszer' && !kervenyId) || (kerelemMod === 'papir' && !kerelemDatum) || (adat.kovetkezo_dijkoteles && dijMod === 'nincs' && !dijMegjegyzes.trim())">
        <ion-icon slot="start" name="ribbon-outline"></ion-icon>{{ fut ? 'Készül…' : 'Kiállítás és nyomtatás' }}
      </ion-button>
    </div>
  </ng-container>
</ion-content>`,
  styles: [`
    h3 { font-size: 1rem; font-weight: 600; margin: 18px 0 6px; }
    .halvany { color: var(--ion-color-medium); font-size: 0.85rem; }
    .figyel { color: var(--ion-color-warning-shade); }
    .piros { color: var(--ion-color-danger); font-size: 0.85rem; }
    .dp { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 8px;
          border: 1px solid rgba(var(--ion-color-medium-rgb), 0.3); border-left: 4px solid var(--ion-color-success); border-radius: 6px; padding: 8px 12px; margin: 6px 0; }
    .dp.ervenytelen { border-left-color: var(--ion-color-medium); opacity: 0.75; }
    .gombok { display: flex; justify-content: flex-end; flex-wrap: wrap; gap: 6px; }
    .dij { font-size: 0.85rem; }
    .dij.fizetve { color: var(--ion-color-success-shade); }
    .dij.fizetendo, .dij.dijbekero, .dij.nincs_kiirva, .dij.torolve { color: var(--ion-color-warning-shade); }
    .dij.visszavonva { color: var(--ion-color-medium); }
  `],
})
export class DiszpeldanyModalComponent implements OnInit {
  @Input() dokumentumId = '';
  adat: any = null;
  betolt = false;
  fut = false;
  kerelemMod = '';
  kervenyId = '';
  kerelemDatum = '';
  formatum = 'A4';
  /** A díjköteles példány díja (backend/diszpeldany-dij.js): 'egyenleg' | 'befizetve' | 'nincs'. */
  dijMod: 'egyenleg' | 'befizetve' | 'nincs' = 'egyenleg';
  dijMegjegyzes = '';

  constructor(private http: HttpClient, private data: DataService, private modal: ModalController, private alert: AlertController) {}

  ngOnInit() { this.toltes(); }
  bezar() { this.modal.dismiss(); }

  /** A kiválasztott kérvény (a határozat jelzéséhez). */
  get valasztottKerveny(): any {
    return (this.adat?.kervenyek || []).find((k: any) => String(k.id) === String(this.kervenyId)) || null;
  }

  async toltes() {
    this.betolt = true;
    try {
      this.adat = await firstValueFrom(this.http.get(`/api/diszpeldany/bizonyitvany/${this.dokumentumId}`));
      if (!this.kerelemMod) this.kerelemMod = this.adat.kervenyek?.length ? 'rendszer' : 'papir';
      if (!this.kervenyId && this.adat.kervenyek?.length) this.kervenyId = this.adat.kervenyek[0].id;
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Nem sikerült betölteni a díszpéldányokat.', 'error');
    } finally { this.betolt = false; }
  }

  async kiallit() {
    if (this.adat?.kovetkezo_dijkoteles) {
      const a = await this.alert.create({
        header: 'Térítéses példány',
        message: `Ebből a bizonyítványból már van érvényes díszpéldány, ez a következő, térítéses példány lesz`
          + `${this.adat?.kovetkezo_dij?.osszeg ? ` (${this.adat.kovetkezo_dij.osszeg} Ft)` : ''}. Kiállítod?`,
        buttons: [{ text: 'Mégse', role: 'cancel' }, { text: 'Kiállítás', role: 'ok' }],
      });
      await a.present();
      if ((await a.onDidDismiss()).role !== 'ok') return;
    }
    this.fut = true;
    try {
      const r: any = await firstValueFrom(this.http.post('/api/diszpeldany/kiallit', {
        dokumentum_id: this.dokumentumId, kerelem_mod: this.kerelemMod, kerveny_id: this.kervenyId,
        kerelem_datum: this.kerelemDatum, formatum: this.formatum, alap: window.location.origin,
        dij_mod: this.dijMod, dij_megjegyzes: this.dijMegjegyzes,
      }));
      this.data.sendToast(`Díszpéldány kiállítva: ${r.azonosito}`, 'success');
      //  A díszpéldány kész; ha a díj nem került az egyenlegbe (pl. nincs családhoz rendelve), külön szólunk.
      if (r?.dij?.hiba) this.data.sendToast(r.dij.hiba, 'error');
      await this.pdf({ id: r.id, azonosito: r.azonosito });
      await this.toltes();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Nem sikerült kiállítani a díszpéldányt.', 'error');
    } finally { this.fut = false; }
  }

  dijAllapotNev(dij: any): string {
    switch (dij?.allapot) {
      case 'fizetve': return `befizetve${dij.datum ? ' (' + dij.datum + ')' : ''}`;
      case 'fizetendo': return 'fizetendő – a család egyenlegében';
      case 'dijbekero': return 'díjbekérő kiállítva, még nincs befizetve';
      case 'visszavonva': return 'visszavonva (érvénytelenítés)';
      case 'torolve': return 'a kiírást törölték az egyenlegből';
      case 'nincs_kiirva': return 'nincs kiírva';
      default: return '';
    }
  }

  async pdf(d: { id: string; azonosito: string }) {
    try {
      const blob = await firstValueFrom(this.http.get(`/api/diszpeldany/pdf/${d.id}`, { responseType: 'blob' }));
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `diszpeldany-${d.azonosito}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (_) { this.data.sendToast('Nem sikerült letölteni a díszpéldányt.', 'error'); }
  }

  async ervenytelenit(d: any) {
    const a = await this.alert.create({
      header: 'Díszpéldány érvénytelenítése',
      message: `${d.azonosito} – az ellenőrző oldal ezután érvénytelennek mutatja. Hibás nyomtatásnál a következő példány nem lesz térítéses.`,
      inputs: [{ name: 'ok', type: 'text', placeholder: 'Ok (pl. hibás nyomtatás, elveszett)' }],
      buttons: [{ text: 'Mégse', role: 'cancel' }, { text: 'Érvénytelenítés', role: 'ok' }],
    });
    await a.present();
    const { role, data } = await a.onDidDismiss();
    if (role !== 'ok') return;
    try {
      const r: any = await firstValueFrom(this.http.post(`/api/diszpeldany/ervenytelenit/${d.id}`, { ok: data?.values?.ok || '' }));
      const dijUzenet = [r?.dij?.visszavonva ? 'A még be nem fizetett díj jóváírással visszavonva.' : '', r?.dij?.figyelmeztetes || ''].filter(Boolean).join(' ');
      if (dijUzenet) this.data.sendToast(dijUzenet, r?.dij?.figyelmeztetes ? 'warning' : 'success');
      await this.toltes();
    } catch (e: any) { this.data.sendToast(e?.error?.error || 'Nem sikerült érvényteleníteni.', 'error'); }
  }
}
